<template>
  <div>
    <section class="hero" v-if="!propio && !sinTitulo">
      <div class="hero-body">
        <div class="titulo-con-guia">
          <p class="title">{{ t("Ventas QR", "QR Sales") }}</p>
          <GuiaRecorrido ref="guia" id="qr-ventas" :pasos="pasosGuia" />
        </div>
        <p class="subtitle is-6">{{ t("Ventas hechas por el QR de cada promotor, para comisiones y trazabilidad.", "Sales made through each promoter's QR, for commissions and traceability.") }}</p>
      </div>
    </section>

    <div class="columns is-multiline is-vcentered" data-guia="v-filtros">
      <div class="column is-3" v-if="!propio">
        <b-field :label="t('Promotor', 'Promoter')" label-position="on-border">
          <b-select v-model="filtros.promotorId" expanded>
            <option :value="null">{{ t("Todos", "All") }}</option>
            <option v-for="p in promotores" :key="p._id" :value="p._id">{{ nombrePromotor(p) }}</option>
          </b-select>
        </b-field>
      </div>
      <div class="column is-3" v-if="!propio && esAdmin && supervisores.length">
        <b-field :label="t('Equipo de', 'Team of')" label-position="on-border">
          <b-select v-model="filtros.supervisorId" expanded>
            <option :value="null">{{ t("Todos", "All") }}</option>
            <option v-for="s in supervisores" :key="s._id" :value="s._id">{{ nombrePromotor(s) }}</option>
          </b-select>
        </b-field>
      </div>
      <div class="column is-3">
        <b-field :label="t('Campaña', 'Campaign')" label-position="on-border">
          <b-select v-model="filtros.campanaId" expanded>
            <option :value="null">{{ t("Todas", "All") }}</option>
            <option v-for="c in campanas" :key="c._id" :value="c._id">{{ c.Nombre && c.Nombre[iso === "es" ? "es" : "en"] }}</option>
          </b-select>
        </b-field>
      </div>
      <div class="column is-3" data-guia="v-estado">
        <b-field :label="t('Estado', 'Status')" label-position="on-border">
          <b-select v-model="filtros.estado" expanded>
            <option value="completadas">{{ t("Pagadas y registros", "Paid and registrations") }}</option>
            <option value="pagada">{{ t("Pagadas", "Paid") }}</option>
            <option value="registrada">{{ t("Registros gratis", "Free registrations") }}</option>
            <option value="pendiente">{{ t("Pendientes (sin pagar)", "Pending (unpaid)") }}</option>
            <option value="verificando">{{ t("Registros sin verificar", "Unverified registrations") }}</option>
            <option value="reembolsada">{{ t("Reembolsadas", "Refunded") }}</option>
            <option value="todas">{{ t("Todas", "All") }}</option>
          </b-select>
        </b-field>
      </div>
      <div class="column is-2">
        <b-field :label="t('Desde', 'From')" label-position="on-border">
          <b-datepicker v-model="filtros.desde" icon="calendar" icon-pack="fas" :locale="iso" editable></b-datepicker>
        </b-field>
      </div>
      <div class="column is-2">
        <b-field :label="t('Hasta', 'To')" label-position="on-border">
          <b-datepicker v-model="filtros.hasta" icon="calendar" icon-pack="fas" :locale="iso" editable></b-datepicker>
        </b-field>
      </div>
      <div class="column is-narrow" data-guia="v-buscar">
        <b-button type="is-info" icon-left="search" icon-pack="fas" :loading="cargando" @click="cargar">{{ t("Buscar", "Search") }}</b-button>
      </div>
      <div class="column is-narrow" data-guia="v-excel">
        <b-button type="is-success" icon-left="file-excel" icon-pack="fas" :loading="exportando" :disabled="!ventas.length" @click="exportar">{{ t("Exportar Excel", "Export Excel") }}</b-button>
      </div>
    </div>

    <div class="resumen-qr" data-guia="v-resumen">
      <span><b>{{ ventas.length }}</b> {{ ventas.length === 1 ? t("venta", "sale") : t("ventas", "sales") }}</span>
      <span v-if="['pagada', 'completadas', 'todas'].includes(filtros.estado)">· {{ t("Total pagado", "Total paid") }}: <b>{{ totalPagado }}</b></span>
      <span v-if="registrosGratis">· {{ t("Registros gratis", "Free registrations") }}: <b>{{ registrosGratis }}</b></span>
      <span v-if="ventas.length >= 5000" class="has-text-danger">· {{ t("Se muestran las primeras 5000; acota las fechas o exporta.", "Showing the first 5000; narrow the dates or export.") }}</span>
    </div>

    <div data-guia="v-tabla">
    <b-table :data="ventas" :loading="cargando" striped hoverable narrowed paginated per-page="20" default-sort="FechaCreacion" default-sort-direction="desc">
      <b-table-column field="FechaCreacion" :label="t('Fecha', 'Date')" sortable v-slot="props">
        <span class="is-size-7">{{ fecha(props.row.FechaPago || props.row.FechaCreacion) }}</span>
      </b-table-column>
      <b-table-column v-if="!propio" field="PromotorUsuario" :label="t('Promotor', 'Promoter')" sortable v-slot="props">
        {{ props.row.PromotorUsuario }}
      </b-table-column>
      <b-table-column v-if="!propio && esAdmin" field="SupervisorUsuario" :label="t('Supervisor', 'Supervisor')" sortable v-slot="props">
        <span :class="{ 'has-text-grey': !props.row.SupervisorUsuario }">{{ props.row.SupervisorUsuario || "—" }}</span>
      </b-table-column>
      <b-table-column field="CampanaNombre" :label="t('Campaña', 'Campaign')" sortable v-slot="props">
        {{ props.row.CampanaNombre }}
      </b-table-column>
      <b-table-column :label="t('Cliente', 'Customer')" v-slot="props">
        <strong>{{ props.row.Cliente.Nombre }} {{ props.row.Cliente.Apellido }}</strong>
        <p class="is-size-7">{{ props.row.Cliente.Email }} · {{ props.row.Cliente.Telefono }} · {{ (props.row.Cliente.Idioma || "").toUpperCase() }}</p>
      </b-table-column>
      <b-table-column field="Precio" :label="t('Monto', 'Amount')" numeric sortable v-slot="props">
        <template v-if="props.row.Gratis">
          <b-tag type="is-success is-light">{{ t("Gratis", "Free") }}</b-tag>
          <p class="is-size-7 has-text-grey">{{ t("valor", "value") }} ${{ Number(props.row.ValorReferencia || 0).toFixed(2) }}</p>
        </template>
        <template v-else>${{ Number(props.row.Precio).toFixed(2) }} {{ (props.row.Moneda || "usd").toUpperCase() }}</template>
      </b-table-column>
      <b-table-column field="Estado" :label="t('Estado', 'Status')" centered sortable v-slot="props">
        <b-tag :type="tipoEstado(props.row.Estado)">{{ etiquetaEstado(props.row.Estado) }}</b-tag>
      </b-table-column>
      <b-table-column :label="t('Entrega', 'Delivery')" v-slot="props">
        <span v-if="!['pagada', 'registrada'].includes(props.row.Estado)" class="has-text-grey">—</span>
        <span v-else-if="props.row.Entrega && props.row.Entrega.Error" class="has-text-danger is-size-7">⚠ {{ props.row.Entrega.Error }}</span>
        <span v-else-if="props.row.Entrega && props.row.Entrega.CertificadoId" class="is-size-7">
          {{ props.row.Entrega.CertificadoId }} · {{ props.row.Entrega.EmailEnviado ? t("correo enviado", "email sent") : t("sin correo", "no email") }}
        </span>
        <span v-else class="has-text-grey is-size-7">{{ t("Solo venta", "Sale only") }}</span>
      </b-table-column>
      <template #empty>
        <p class="has-text-centered has-text-grey">{{ t("No hay ventas con estos filtros", "No sales for these filters") }}</p>
      </template>
    </b-table>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import apiQr, { mensajeError, descargar } from "../../utils/apiQr";
import GuiaRecorrido from "../../components/GuiaRecorrido.vue";
import { pasosVentas } from "./guiaQr";

export default {
  name: "VentasQr",
  components: { GuiaRecorrido },
  props: {
    // true en "Mis clientes": la API ya limita al promotor en sesión
    propio: { type: Boolean, default: false },
    // true cuando va dentro de otra pantalla que ya tiene su título (Mis clientes del admin)
    sinTitulo: { type: Boolean, default: false },
  },
  data() {
    return {
      iso: localStorage.getItem("iso") || "es",
      // Si no es admin y entra aquí, es un supervisor: la API le devuelve sus ventas y las de su equipo
      esAdmin: ["admin", "ultrauser"].includes(localStorage.getItem("role")),
      ventas: [],
      promotores: [],
      campanas: [],
      cargando: false,
      exportando: false,
      filtros: {
        promotorId: null,
        supervisorId: null,
        campanaId: null,
        estado: "completadas",
        desde: moment().startOf("month").toDate(),
        hasta: new Date(),
      },
    };
  },
  computed: {
    supervisores() {
      return this.promotores.filter((p) => p.Tipo === "supervisor");
    },
    registrosGratis() {
      return this.ventas.filter((v) => v.Estado === "registrada").length;
    },
    // Solo cuando es la pantalla "Ventas QR"; dentro de "Mis clientes" la guía es la de esa pantalla
    pasosGuia() {
      const intro = {
        el: null,
        titulo: this.t("Ventas QR", "QR Sales"),
        texto: this.esAdmin
          ? this.t("Aquí ves todo lo que entra por los QR: ventas pagadas y registros gratis de cada promotor. Te mostramos cómo filtrar y exportar.", "Here you see everything that comes in through the QRs: paid sales and free registrations of each promoter. Let's see how to filter and export.")
          : this.t("Aquí ves lo que vendieron tú y los agentes de tu equipo con sus QR. Te mostramos cómo filtrar y exportar.", "Here you see what you and your team's agents sold with their QRs. Let's see how to filter and export."),
      };
      return [intro, ...pasosVentas(this.t)];
    },
    totalPagado() {
      const total = this.ventas.filter((v) => v.Estado === "pagada").reduce((s, v) => s + Number(v.Precio || 0), 0);
      return `$${total.toFixed(2)} USD`;
    },
  },
  created() {
    this.$bus.on("change-iso", () => (this.iso = localStorage.getItem("iso")));
    this.cargarCatalogos();
    this.cargaInicial = this.cargar();
  },
  mounted() {
    // La guía se abre sola la primera vez, ya con la lista cargada
    if (this.propio || this.sinTitulo) return;
    this.cargaInicial.then(() => this.$nextTick(() => this.$refs.guia && this.$refs.guia.autoIniciar()));
  },
  methods: {
    t(es, en) {
      return this.iso === "es" ? es : en;
    },
    fecha(ms) {
      return ms ? moment(ms).format("DD/MM/YYYY HH:mm") : "";
    },
    nombrePromotor(p) {
      const u = p.Usuario || {};
      return [u.Nombre, u.Apellido].filter(Boolean).join(" ") || u.Usuario || p.Codigo;
    },
    etiquetaEstado(e) {
      return {
        pagada: this.t("Pagada", "Paid"),
        pendiente: this.t("Pendiente", "Pending"),
        reembolsada: this.t("Reembolsada", "Refunded"),
        expirada: this.t("Expirada", "Expired"),
        registrada: this.t("Registrada", "Registered"),
        verificando: this.t("Sin verificar", "Unverified"),
      }[e] || e;
    },
    tipoEstado(e) {
      return { pagada: "is-success", registrada: "is-success", pendiente: "is-warning is-light", verificando: "is-warning is-light", reembolsada: "is-danger is-light" }[e] || "is-light";
    },
    params() {
      const f = this.filtros;
      const p = { estado: f.estado };
      // En "Mis clientes" un supervisor ve solo lo que vendió con su QR, no lo de su equipo
      if (this.propio) p.solo = "propias";
      if (!this.propio && f.promotorId) p.promotorId = f.promotorId;
      if (!this.propio && this.esAdmin && f.supervisorId) p.supervisorId = f.supervisorId;
      if (f.campanaId) p.campanaId = f.campanaId;
      if (f.desde) p.desde = moment(f.desde).format("YYYY-MM-DD");
      if (f.hasta) p.hasta = moment(f.hasta).format("YYYY-MM-DD");
      return p;
    },
    async cargarCatalogos() {
      try {
        const { data } = await apiQr.get("/campanas");
        this.campanas = data;
        if (!this.propio) this.promotores = (await apiQr.get("/promotores")).data;
      } catch (error) {
        console.log(error);
      }
    },
    async cargar() {
      try {
        this.cargando = true;
        const { data } = await apiQr.get("/ventas", { params: this.params() });
        this.ventas = data;
      } catch (error) {
        this.ventas = [];
        this.$buefy.toast.open({ message: mensajeError(error, this.t("Error al cargar ventas", "Error loading sales")), type: "is-danger" });
      } finally {
        this.cargando = false;
      }
    },
    async exportar() {
      try {
        this.exportando = true;
        await descargar("/ventas/excel", `ventas-qr-${moment().format("YYYY-MM-DD")}.xlsx`, this.params());
      } catch (error) {
        this.$buefy.toast.open({ message: mensajeError(error, this.t("No se pudo exportar", "Could not export")), type: "is-danger" });
      } finally {
        this.exportando = false;
      }
    },
  },
};
</script>

<style scoped>
.resumen-qr {
  background: #f5f7fa;
  border-radius: 6px;
  padding: 0.5rem 0.8rem;
  margin-bottom: 0.8rem;
  font-size: 0.9rem;
}
.resumen-qr span + span {
  margin-left: 0.4rem;
}
</style>
